import React from "react";
import { Eye, Download, CreditCard } from "lucide-react";

export default function InvoiceTable({ invoices, onViewInvoiceClick, onDownloadInvoiceClick, isScrollable = false, currencySymbol }) {
  // Helper to format status badges
  const getStatusBadge = (status = "") => {
    const s = status.toLowerCase();
    switch (s) {
      case "paid":
        return "bg-success-bg text-success border-success/20";
      case "pending":
        return "bg-warning-bg text-warning border-warning/20";
      case "failed":
        return "bg-brand-red-light text-brand-red border-brand-red/10";
      case "refunded":
        return "bg-blue-50 text-blue-700 border-blue-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  return (
    <div className="w-full">
      {/* RESPONSIVE TABLE VIEW (TABLE ON ALL SCREENS, NO CARDS) */}
      <div
        className={`w-full overflow-x-auto bg-white rounded-xl border border-border-primary shadow-xs scrollbar-thin ${isScrollable ? "max-h-[420px] overflow-y-auto" : ""
          }`}
      >
        <table className="w-full text-left border-separate border-spacing-0 min-w-[700px] md:min-w-0">
          <thead>
            <tr className="bg-bg-secondary text-[11px] uppercase tracking-wider font-bold text-text-secondary sticky top-0 z-20">
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary">Invoice ID</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary">Date</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary">Order Details</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary">Amount</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary">Payment Method</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary">Status</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary text-sm bg-white">
            {invoices.map((inv) => {
              const billId = inv.bill_unique_id || inv.id;
              const date = inv.bill_date || inv.date;
              const orderDesc = inv.bill_type
                ? `${inv.bill_type.replace('_', ' ')} (${inv.orders_count} order${inv.orders_count > 1 ? 's' : ''})`
                : (inv.orderDesc || "Order bill");
              const totalAmount = inv.total_amount !== undefined ? inv.total_amount : (inv.amount * 30);
              const paymentMethod = inv.paymentMethod
                ? inv.paymentMethod.replace("ending in", "#")
                : (inv.paid_amount > 0 ? "Paid online" : "Due / Unpaid");
              const statusStr = inv.status ? (inv.status.charAt(0).toUpperCase() + inv.status.slice(1)) : "";

              return (
                <tr
                  key={billId}
                  className="hover:bg-bg-secondary/40 transition-colors p-2"
                >
                  <td className="py-4 px-6 font-mono font-semibold text-text-primary border-b border-border-primary/60">
                    #{billId}
                  </td>
                  <td className="py-4 px-6 text-text-secondary border-b border-border-primary/60 font-mono text-xs">
                    {date}
                  </td>
                  <td className="py-4 px-6 font-medium text-text-primary border-b border-border-primary/60 capitalize">
                    {orderDesc}
                  </td>
                  <td className="py-4 px-6 font-bold text-text-primary font-mono border-b border-border-primary/60">
                    {inv.currency_symbol || currencySymbol}{totalAmount.toFixed(2)}
                  </td>
                  <td className="py-4 px-6 text-text-secondary border-b border-border-primary/60">
                    <div className="flex items-center space-x-1.5">
                      <CreditCard className="w-4 h-4 text-text-muted shrink-0" />
                      <span>{paymentMethod}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6 border-b border-border-primary/60">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(inv.status)}`}>
                      {statusStr}
                    </span>
                  </td>
                  <td className="py-4 px-6 border-b border-border-primary/60">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => onViewInvoiceClick(inv)}
                        title="View Invoice Details"
                        className="p-1.5 text-brand-red hover:text-brand-red-hover hover:bg-brand-red-light/20 rounded-lg transition-colors cursor-pointer"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDownloadInvoiceClick && onDownloadInvoiceClick(inv.bill_unique_id || inv.id, inv)}
                        title="Download Invoice"
                        className="p-1.5 text-brand-red hover:text-brand-red-hover hover:bg-brand-red-light/20 rounded-lg transition-colors cursor-pointer"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
